import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Label, Textarea, Empty } from "@/components/Primitives";
import { Plus, X, Pencil, Trash2, Rocket, Hammer, Lightbulb, Calendar, EyeOff, Tag, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const COLUMNS = [
  { key: "planned", label: "Planned", icon: Lightbulb, tint: "hsl(var(--primary))" },
  { key: "in_progress", label: "In progress", icon: Hammer, tint: "hsl(var(--accent))" },
  { key: "shipped", label: "Recently shipped", icon: Rocket, tint: "hsl(var(--success))" },
];

const KINDS = [
  { v: "feature", l: "Feature" },
  { v: "improvement", l: "Improvement" },
  { v: "maintenance", l: "Maintenance" },
  { v: "bug_fix", l: "Bug fix" },
  { v: "integration", l: "Integration" },
];

function formatTimelineDate(item) {
  // Shipped → real ship date "MMM D"
  if (item.status === "shipped" && item.shipped_date) {
    const d = new Date(item.shipped_date);
    if (!isNaN(d)) return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  // ETA: if it parses as a date, format it; otherwise show the free-text ETA as-is.
  if (item.eta) {
    const d = new Date(item.eta);
    if (!isNaN(d) && /\d{4}/.test(item.eta)) {
      return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    }
    return item.eta;
  }
  // Last fallback: when was it added?
  if (item.created_at) {
    const d = new Date(item.created_at);
    if (!isNaN(d)) return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return null;
}

export default function RoadmapPage() {
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [productFilter, setProductFilter] = useState(null);

  const load = async () => {
    const { data } = await api.get("/roadmap");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  // All unique product tags for filter chips
  const allTags = Array.from(new Set(items.flatMap((i) => i.product_tags || []))).sort();
  const filtered = productFilter
    ? items.filter((i) => (i.product_tags || []).includes(productFilter) || (i.product_tags || []).includes("general"))
    : items;

  const grouped = {
    planned: filtered.filter((i) => i.status === "planned"),
    in_progress: filtered.filter((i) => i.status === "in_progress"),
    shipped: filtered.filter((i) => i.status === "shipped").sort((a, b) => (b.shipped_date || "").localeCompare(a.shipped_date || "")),
  };

  const onDrop = async (col) => async (e) => {
    e.preventDefault();
    if (!dragId || isCustomer) return;
    const item = items.find((i) => i.id === dragId);
    setDragId(null);
    if (!item || item.status === col) return;
    // Optimistic
    setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, status: col } : i));
    try {
      await api.patch(`/roadmap/${item.id}`, { status: col });
      load();
    } catch (err) {
      toast.error("Failed to move item");
      load();
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try { await api.delete(`/roadmap/${r.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={isCustomer ? "My account · Roadmap" : "Studio · Roadmap"}
        title={isCustomer ? "What's coming up" : "Roadmap"}
        subtitle={isCustomer ? "The road ahead, plus what we've just shipped for you." : "Three columns. Drag to move. Customers see non-internal items."}
        actions={!isCustomer && <Button onClick={() => setShowNew(true)} data-testid="new-roadmap-btn"><Plus className="w-4 h-4" strokeWidth={2}/>Add item</Button>}
      />

      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mr-1 inline-flex items-center gap-1"><Tag className="w-3 h-3" strokeWidth={2}/>Filter:</span>
          <button onClick={() => setProductFilter(null)} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all ${!productFilter ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`}>All</button>
          {allTags.map((t) => (
            <button key={t} onClick={() => setProductFilter(t === productFilter ? null : t)} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all ${productFilter === t ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`} data-testid={`roadmap-filter-${t}`}>{t}</button>
          ))}
        </div>
      )}

      {isCustomer ? (
        <CustomerTimeline grouped={grouped} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map((col) => {
          const Icon = col.icon;
          const rows = grouped[col.key] || [];
          return (
            <div
              key={col.key}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={onDrop(col.key)}
              className="rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] overflow-hidden"
              data-testid={`roadmap-col-${col.key}`}
            >
              <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between bg-[hsl(var(--background))]">
                <div className="inline-flex items-center gap-2">
                  <Icon className="w-4 h-4" strokeWidth={1.8} style={{ color: col.tint }}/>
                  <h3 className="font-semibold text-[13px]">{col.label}</h3>
                </div>
                <span className="text-[11px] font-mono text-[hsl(var(--text-muted))]">{rows.length}</span>
              </div>
              <div className="p-3 space-y-2.5 min-h-[180px]">
                {rows.map((r) => (
                  <div
                    key={r.id}
                    draggable={!isCustomer}
                    onDragStart={() => setDragId(r.id)}
                    onDragEnd={() => setDragId(null)}
                    className={`group rounded-xl border border-[hsl(var(--border))] p-3 bg-[hsl(var(--background))] hover:border-[hsl(var(--primary))] ${!isCustomer ? "cursor-move" : ""} transition-all ${dragId === r.id ? "opacity-50" : ""}`}
                    data-testid="roadmap-card"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold leading-tight">{r.title}</div>
                        {r.description && <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed">{r.description}</div>}
                      </div>
                      {!isCustomer && (
                        <div className="flex gap-0.5 opacity-60 hover:opacity-100 transition-opacity">
                          <button onClick={() => setEditing(r)} className="p-1 rounded hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]" title="Edit" data-testid="edit-roadmap-btn"><Pencil className="w-3 h-3" strokeWidth={1.6}/></button>
                          <button onClick={() => remove(r)} className="p-1 rounded hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600" title="Delete"><Trash2 className="w-3 h-3" strokeWidth={1.6}/></button>
                        </div>
                      )}
                    </div>
                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]">{(KINDS.find((k) => k.v === r.kind)?.l || r.kind).toUpperCase()}</span>
                      {(r.product_tags || []).map((t) => (
                        <span key={t} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]">#{t}</span>
                      ))}
                      {r.eta && col.key !== "shipped" && (
                        <span className="ml-auto text-[10px] text-[hsl(var(--text-muted))] font-mono inline-flex items-center gap-1"><Calendar className="w-2.5 h-2.5" strokeWidth={2}/>{r.eta}</span>
                      )}
                      {col.key === "shipped" && r.shipped_date && (
                        <span className="ml-auto text-[10px] text-[hsl(var(--success))] font-mono inline-flex items-center gap-1">✓ {new Date(r.shipped_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                      )}
                      {r.internal && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 inline-flex items-center gap-1" title="Not visible to customers"><EyeOff className="w-2.5 h-2.5" strokeWidth={2}/>Internal</span>}
                    </div>
                  </div>
                ))}
                {rows.length === 0 && (
                  <div className="text-[12px] text-[hsl(var(--text-muted))] italic text-center py-6">{col.key === "shipped" ? "Nothing shipped recently." : isCustomer ? "Nothing here yet." : "Drop items here."}</div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      )}

      {showNew && <RoadmapFormModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <RoadmapFormModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CustomerTimeline({ grouped }) {
  // Combine upcoming (planned + in_progress) and shipped. Build a single left→right timeline.
  const upcoming = [
    ...grouped.planned.map((i) => ({ ...i, phase: "planned" })),
    ...grouped.in_progress.map((i) => ({ ...i, phase: "in_progress" })),
  ];
  // Shipped: newest first when sorted, but render left→right chronologically (oldest→newest) so "today" lives on the right.
  const shipped = [...grouped.shipped].sort((a, b) => (a.shipped_date || "").localeCompare(b.shipped_date || ""));

  const hasUpcoming = upcoming.length > 0;
  const hasShipped = shipped.length > 0;

  if (!hasUpcoming && !hasShipped) {
    return (
      <Card>
        <Empty title="Nothing on the roadmap yet" subtitle="Your account manager will share plans here as soon as we start mapping your next phase." />
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-timeline">
      {hasShipped && <TimelineStrip title="Recently shipped" accent="success" items={shipped} direction="rtl" />}
      {hasUpcoming && <TimelineStrip title="What's coming" accent="primary" items={upcoming} direction="ltr" />}
    </div>
  );
}

function TimelineStrip({ title, accent, items, direction }) {
  // direction: "rtl" means newest on the left (shipped list is old→new, so we reverse); "ltr" means we keep given order.
  const rows = direction === "rtl" ? [...items].reverse() : items;
  const tint = accent === "success" ? "hsl(var(--success))" : "hsl(var(--primary))";
  const tintMuted = accent === "success" ? "hsl(var(--success) / 0.15)" : "hsl(var(--primary-muted))";

  return (
    <Card>
      <div className="px-5 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          {accent === "success" ? <Rocket className="w-4 h-4" strokeWidth={1.8} style={{ color: tint }}/> : <Lightbulb className="w-4 h-4" strokeWidth={1.8} style={{ color: tint }}/>}
          <h3 className="font-semibold text-[13px]">{title}</h3>
          <span className="text-[11px] font-mono text-[hsl(var(--text-muted))]">{items.length}</span>
        </div>
      </div>

      <div className="relative overflow-x-auto">
        <div className="min-w-full px-8 pt-10 pb-14" style={{ minWidth: `${Math.max(rows.length * 280, 600)}px` }}>
          {/* Rail */}
          <div className="absolute left-8 right-8 top-1/2 h-[2px] -translate-y-1/2 rounded-full" style={{ background: `linear-gradient(to right, ${tintMuted}, ${tint}, ${tintMuted})` }}/>

          <div className="relative grid gap-0" style={{ gridTemplateColumns: `repeat(${rows.length}, minmax(240px, 1fr))` }}>
            {rows.map((r, idx) => {
              const above = idx % 2 === 0;
              const dateLabel = formatTimelineDate(r);
              return (
                <div key={r.id} className="relative flex flex-col items-center" data-testid="timeline-item">
                  {/* Card above (even) */}
                  {above && <TimelineCard item={r} accent={accent} position="above" />}
                  {/* Dot on rail with date label */}
                  <div className="relative z-10 my-1 flex flex-col items-center gap-1">
                    <div className="w-4 h-4 rounded-full ring-4 ring-[hsl(var(--surface))] shadow-md flex items-center justify-center" style={{ background: tint }}>
                      {r.status === "shipped" && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3}/>}
                    </div>
                    {dateLabel && (
                      <span
                        className="absolute top-full mt-1.5 whitespace-nowrap text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded"
                        style={{ background: tintMuted, color: tint }}
                        data-testid="timeline-date"
                      >
                        {dateLabel}
                      </span>
                    )}
                  </div>
                  {/* Card below (odd) */}
                  {!above && <TimelineCard item={r} accent={accent} position="below" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}

function TimelineCard({ item, accent, position }) {
  const tint = accent === "success" ? "hsl(var(--success))" : "hsl(var(--primary))";
  const kindLabel = (KINDS.find((k) => k.v === item.kind)?.l || item.kind);
  const phaseLabel =
    item.status === "shipped" ? "Shipped" :
    item.status === "in_progress" ? "In progress" :
    "Planned";

  const connector = (
    <div className="w-[2px] h-6 mx-auto" style={{ background: tint, opacity: 0.4 }}/>
  );

  const card = (
    <div className="group rounded-xl border border-[hsl(var(--border))] p-3.5 bg-[hsl(var(--background))] hover:border-[hsl(var(--primary))] transition-all w-[240px] max-w-full shadow-sm hover:shadow-md">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: accent === "success" ? "hsl(var(--success) / 0.15)" : "hsl(var(--primary-muted))", color: tint }}>
          {phaseLabel}
        </span>
        {item.status === "shipped" && item.shipped_date && (
          <span className="text-[10px] text-[hsl(var(--text-muted))] font-mono ml-auto">{new Date(item.shipped_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
        )}
        {item.status !== "shipped" && item.eta && (
          <span className="ml-auto text-[10px] text-[hsl(var(--text-muted))] font-mono inline-flex items-center gap-1"><Calendar className="w-2.5 h-2.5" strokeWidth={2}/>{item.eta}</span>
        )}
      </div>
      <div className="text-[13px] font-semibold leading-tight">{item.title}</div>
      {item.description && <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed line-clamp-3">{item.description}</div>}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]">{kindLabel.toUpperCase()}</span>
        {(item.product_tags || []).slice(0, 2).map((t) => (
          <span key={t} className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]">#{t}</span>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`w-full flex flex-col items-center ${position === "above" ? "mb-1" : "mt-1"}`}>
      {position === "above" && <>{card}{connector}</>}
      {position === "below" && <>{connector}{card}</>}
    </div>
  );
}

function RoadmapFormModal({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState(() => ({
    title: item?.title || "",
    description: item?.description || "",
    status: item?.status || "planned",
    kind: item?.kind || "feature",
    product_tags: (item?.product_tags || []).join(", "),
    eta: item?.eta || "",
    internal: item?.internal || false,
  }));
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        product_tags: form.product_tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
      };
      if (isEdit) { await api.patch(`/roadmap/${item.id}`, payload); toast.success("Updated"); }
      else { await api.post("/roadmap", payload); toast.success("Item added"); }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl" data-testid="roadmap-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isEdit ? "Edit roadmap item" : "New roadmap item"}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div><Label>Title *</Label><Input required value={form.title} onChange={s("title")} placeholder="Dark mode across customer portal" data-testid="roadmap-title"/></div>
          <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={s("description")} placeholder="1-2 sentences. What it is and why customers care."/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Status</Label>
              <Select value={form.status} onChange={s("status")}>
                <option value="planned">Planned</option>
                <option value="in_progress">In progress</option>
                <option value="shipped">Shipped</option>
              </Select>
            </div>
            <div><Label>Kind</Label>
              <Select value={form.kind} onChange={s("kind")}>{KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}</Select>
            </div>
          </div>
          <div><Label>Product tags (comma-separated)</Label><Input value={form.product_tags} onChange={s("product_tags")} placeholder="soubor-one, vantage, general"/></div>
          <div><Label>ETA (free text)</Label><Input value={form.eta} onChange={s("eta")} placeholder="Q2 2026, Soon, Jun 2026…"/></div>
          <label className="inline-flex items-center gap-2 text-[12px] text-[hsl(var(--text-muted))] cursor-pointer select-none" data-testid="roadmap-internal-toggle">
            <input type="checkbox" checked={form.internal} onChange={(e) => setForm({ ...form, internal: e.target.checked })} className="accent-[hsl(var(--primary))]"/>
            Internal only (hidden from customers)
          </label>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="roadmap-save-btn">{saving ? "Saving…" : isEdit ? "Save changes" : "Add item"}</Button>
        </div>
      </form>
    </div>
  );
}
